import { put, call, select } from 'redux-saga/effects';
import * as SagaKit from 'redux-saga-kit';
import _ from 'lodash';
import { types, actions } from '../actions/SettingsActions';
import { types as StyleTypes } from '@wix/restaurants-redux/dist/actions/StylesActions';
import { PARAMS_KEYS } from '../shared/constants';
import { I18N } from '@wix/restaurants-i18n-js';
import TranslatedContent from '@wix/wixrest-utils/dist/TranslatedContent';
import TOKENS from '../tokens';

const HEADER = 'reservations_app_';
const DEFAULT_ALIGNMENT= 1;

// ============================================================================
// SAGAS
// ============================================================================
const initState = ({Wix}) => function* ({payload}) {
    let {styles} = payload;
    const compId = Wix.Utils.getCompId();
    const showTitle = getBoolean(styles, PARAMS_KEYS.SHOW_TITLE_KEY);
    const showDescription = getBoolean(styles, PARAMS_KEYS.SHOW_DESCRIPTION_KEY);
    const layout = getNumber(styles, PARAMS_KEYS.LAYOUT_KEY);
    const alignment = getNumber(styles, PARAMS_KEYS.ALIGNMENT_KEY, DEFAULT_ALIGNMENT);
    const customDescription = getData(styles, compId, PARAMS_KEYS.CUSTOM_DESCRIPTION_DATA_KEY, '');
    const customTitle = yield call(getTitle({Wix}), styles);
    const customButton = yield call(getButtonText({Wix}), styles);
    const isCustomDesign = styles.numbers[PARAMS_KEYS.SELECTED_PRESET] === -1;

    yield put(actions.initSettings({showTitle, showDescription, layout, alignment, customTitle, customDescription, customButton, isCustomDesign}));
};

const resizeComponent = ({Wix}) => function* ({payload}) {
    if (!_.isEmpty(payload)) {
        try {
            Wix.resizeComponent(payload, () => {
                // console.debug('[RESIZE]: Success');
            });
        } catch (e) {
            /*Not in Editor mode*/
            const windowHeight = yield select(windowHeightSelector);
            if (payload.height && payload.height > windowHeight) {
                Wix.setHeight(payload.height);
            }
        }
    }
};

// ============================================================================
// HELPERS
// ============================================================================

function getBoolean (styles, key, defaultValue) {
    return getStyle(styles, 'booleans', key, defaultValue);
}

function getNumber (styles, key, defaultValue) {
    return getStyle(styles, 'numbers', key, defaultValue);
}

function getData (styles, compId, key, defaultValue) {
    return getStyle(styles, 'data', `${key}.${compId}`, {value: defaultValue}).value;
}

function getStyle (styles, domain, key, defaultValue) {
    return _.get(styles, [domain, key], defaultValue);
}

function* getSelectedTitle (selectedText) {
    const content = yield call(getContent);
    let options = [
        content.get('title_preset1'),
        content.get('title_preset2'),
        content.get('title_preset3'),
        content.get('title_preset4'),
        content.get('title_preset5')
    ];
    return options[selectedText];
}

const getTitle = ({Wix}) => function (styles) {
    const selectedText = styles.numbers[PARAMS_KEYS.SELECTED_TITLE_KEY];
    const isCustom = selectedText === -1;
    const compId = Wix.Utils.getCompId();
    let title = '';

    if (isCustom) {
        title = styles.data[`${PARAMS_KEYS.CUSTOM_TITLE_DATA_KEY}.${compId}`] &&
            styles.data[`${PARAMS_KEYS.CUSTOM_TITLE_DATA_KEY}.${compId}`].value || '';
    } else {
        title = getSelectedTitle(selectedText);
    }

    return title;
}

function* getSelectedButtonText (selectedText) {
    const content = yield call(getContent);
    let options = [
        content.get('button_preset1'),
        content.get('button_preset2'),
        content.get('button_preset3'),
        content.get('button_preset4'),
        content.get('button_preset5')
    ];
    return options[selectedText];
}

const getContent = function* () {
    const i18n = yield select(i18nSelector);
    const content = (new TranslatedContent({header: HEADER, tokens: TOKENS, i18n: new I18N(i18n.localeData)})).instance('');
    return content;
};

const getButtonText = ({Wix}) => function (styles) {
    const selectedButtonText = styles.numbers[PARAMS_KEYS.SELECTED_BUTTON_KEY];
    const isCustom = selectedButtonText === -1;
    const compId = Wix.Utils.getCompId();
    let buttonText = '';

    if (isCustom) {
        buttonText = styles.data[`${PARAMS_KEYS.CUSTOM_BUTTON_DATA_KEY}.${compId}`] &&
            styles.data[`${PARAMS_KEYS.CUSTOM_BUTTON_DATA_KEY}.${compId}`].value || '';
    } else {
        buttonText = getSelectedButtonText(selectedButtonText);
    }

    return buttonText;
}

// ============================================================================
// SELECTORS
// ============================================================================

const i18nSelector = SagaKit.stateSelector('i18nData', {});
const windowHeightSelector = SagaKit.stateSelector('seatingSettings.windowHeight', Number.POSITIVE_INFINITY);

// ============================================================================
// EXPORT
// ============================================================================

export default SagaKit.sagaCreator({
    [StyleTypes.SET_STYLES]: initState,
    [types.RESIZE_COMPONENT]: resizeComponent
});
